import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Dashboard from './pages/Dashboard';
import ProductManagement from './pages/ProductManagement';
import GroupManagement from './pages/GroupManagement';
import GroupMonitor from './pages/GroupMonitor';
import TrafficMonitor from './pages/TrafficMonitor';
import UserManagement from './pages/UserManagement';
import MerchantManagement from './pages/MerchantManagement';
import AgentCenter from './pages/AgentCenter';
import DistributionManagement from './pages/DistributionManagement';
import PaymentManagement from './pages/PaymentManagement';
import SettlementManagement from './pages/SettlementManagement';
import RefundManagement from './pages/RefundManagement';
import Ranking from './pages/Ranking';
import ChannelAnalysis from './pages/ChannelAnalysis';
import Settings from './pages/Settings';
import ServerControl from './pages/ServerControl';
import Broadcast from './pages/Broadcast';
import CustomerService from './pages/CustomerService';
import Login from './pages/Login';
import { useAuthStore } from './store/authStore';

const { Content } = Layout;

const MOBILE_BREAKPOINT = 768;

const App: React.FC = () => {
  const { isAuthenticated, isInitialized, login, initializeAuth } = useAuthStore();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 开发环境下自动登录
  useEffect(() => {
    if (import.meta.env.DEV && isInitialized && !isAuthenticated) {
      void login('admin', '123456');
    }
  }, [isAuthenticated, isInitialized, login]);

  const toggleSidebar = useCallback(() => setSidebarOpen(v => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">
        正在加载登录状态...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout className="layout-container">
      <Sidebar isMobile={isMobile} open={sidebarOpen} onClose={closeSidebar} />
      <Layout>
        <Header isMobile={isMobile} onMenuClick={toggleSidebar} />
        <Content className="layout-content fade-in">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/products" element={<ProductManagement />} />
            <Route path="/groups" element={<GroupManagement />} />
            <Route path="/group-monitor" element={<GroupMonitor />} />
            <Route path="/traffic" element={<TrafficMonitor />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/merchants" element={<MerchantManagement />} />
            <Route path="/agents" element={<AgentCenter />} />
            <Route path="/distribution" element={<DistributionManagement />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/channels" element={<ChannelAnalysis />} />
            <Route path="/payments" element={<PaymentManagement />} />
            <Route path="/settlement" element={<SettlementManagement />} />
            <Route path="/refunds" element={<RefundManagement />} />
            <Route path="/broadcast" element={<Broadcast />} />
            <Route path="/customer-service" element={<CustomerService />} />
            <Route path="/server" element={<ServerControl />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
